// src/components/HireAssistanceBubble.jsx
// Modern navigation bubble for DriverTrip - EN and IP stages only
// Fully responsive with mobile-optimized placement

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Navigation, 
  MapPin, 
  Flag, 
  AlertTriangle,
  X,
  ChevronRight,
  Car,
  Compass,
  ArrowRight,
  Phone,
  Shield,
  Clock,
  Navigation2,
  Move
} from 'lucide-react';

const HireAssistanceBubble = ({
  tripStage = null, // "heading_to_pickup" (EN) or "trip_started" (IP)
  pickupLocation = null, // { lat, lng }
  destinationLocation = null, // { lat, lng }
  driverLocation = null, // { lat, lng }
  onEmergency = () => {},
  estimatedTime = null,
  estimatedDistance = null,
}) => {
  // Dragging state
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 180 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const bubbleRef = useRef(null);

  // Only show for EN (heading_to_pickup) or IP (trip_started) stages
  const isEnRoute = tripStage === "heading_to_pickup";
  const isInProgress = tripStage === "trip_started";
  const shouldShow = isEnRoute || isInProgress;

  // Check for mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Reposition on resize to ensure visibility
      const maxY = window.innerHeight - 100;
      setPosition(prev => ({
        x: Math.min(Math.max(10, prev.x), window.innerWidth - 80),
        y: Math.min(Math.max(60, prev.y), maxY)
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load saved position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem("hire_bubble_position");
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        const maxX = window.innerWidth - 80;
        const maxY = window.innerHeight - 100;
        setPosition({
          x: Math.min(Math.max(10, pos.x), maxX),
          y: Math.min(Math.max(60, pos.y), maxY),
        });
      } catch {
        // Use default position - bottom right for mobile
        setPosition({ 
          x: isMobile ? window.innerWidth - 80 : 20, 
          y: window.innerHeight - 180 
        });
      }
    }
  }, [isMobile]);

  // Handle touch/mouse start
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;

    setIsDragging(true);
    setDragOffset({
      x: clientX - position.x,
      y: clientY - position.y,
    });
    setIsPulsing(false);
  };

  // Handle touch/mouse move
  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();

      const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;

      let newX = clientX - dragOffset.x;
      let newY = clientY - dragOffset.y;

      // Keep bubble within viewport with safe margins
      const maxX = window.innerWidth - (isMobile ? 70 : 80);
      const maxY = window.innerHeight - (isMobile ? 100 : 80);
      const minX = 10;
      const minY = isMobile ? 50 : 60;

      newX = Math.min(Math.max(minX, newX), maxX);
      newY = Math.min(Math.max(minY, newY), maxY);

      setPosition({ x: newX, y: newY });
    },
    [isDragging, dragOffset, isMobile]
  );

  // Handle touch/mouse end
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setIsPulsing(true);
      
      // Snap to edge - but keep within safe zone
      const midX = window.innerWidth / 2;
      const safeEdge = isMobile ? 15 : 10;
      const snapX = position.x < midX ? safeEdge : window.innerWidth - (isMobile ? 75 : 80);
      const newPosition = { ...position, x: snapX };
      setPosition(newPosition);
      localStorage.setItem("hire_bubble_position", JSON.stringify(newPosition));
    }
  }, [isDragging, position, isMobile]);

  // Add global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Open Google Maps with correct route
  const handleOpenMaps = () => {
    let fromLat, fromLng, toLat, toLng;

    if (isEnRoute) {
      if (!driverLocation || !pickupLocation) return;
      fromLat = driverLocation.lat;
      fromLng = driverLocation.lng;
      toLat = pickupLocation.lat;
      toLng = pickupLocation.lng;
    } else if (isInProgress) {
      if (!pickupLocation || !destinationLocation) return;
      fromLat = pickupLocation.lat;
      fromLng = pickupLocation.lng;
      toLat = destinationLocation.lat;
      toLng = destinationLocation.lng;
    } else {
      return;
    }

    const fromLocation = `${fromLat},${fromLng}`;
    const toLocation = `${toLat},${toLng}`;

    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(userAgent);
    const isIOS = /iphone|ipad|ipod/.test(userAgent);

    if (isAndroid) {
      const nativeUrl = `google.navigation:q=${toLocation}&mode=d`;
      const fallbackUrl = `https://www.google.com/maps/dir/?api=1&origin=${fromLocation}&destination=${toLocation}&travelmode=driving`;
      window.location.href = nativeUrl;
      setTimeout(() => { window.location.href = fallbackUrl; }, 500);
    } else if (isIOS) {
      const url = `maps://maps.apple.com/?saddr=${fromLocation}&daddr=${toLocation}&dirflg=d`;
      window.location.href = url;
    } else {
      const url = `https://www.google.com/maps/dir/?api=1&origin=${fromLocation}&destination=${toLocation}&travelmode=driving`;
      window.open(url, '_blank');
    }

    setIsExpanded(false);
  };

  // Handle emergency alert
  const handleEmergency = () => {
    onEmergency();
    setIsExpanded(false);
  };

  // Get stage-specific info with project colors
  const getStageInfo = () => {
    if (isEnRoute) {
      return {
        icon: Navigation,
        label: "To Pickup",
        shortLabel: "Pickup",
        description: "Navigate to pickup location",
        color: "from-blue-500 to-blue-600",
        badgeColor: "bg-blue-100 text-blue-700",
        buttonColor: "bg-blue-600 hover:bg-blue-700",
        lightBg: "bg-blue-50",
        iconBg: "bg-blue-100",
        progressLabel: "Heading to pickup"
      };
    } else if (isInProgress) {
      return {
        icon: Flag,
        label: "To Drop",
        shortLabel: "Drop",
        description: "Navigate to destination",
        color: "from-purple-500 to-purple-600",
        badgeColor: "bg-purple-100 text-purple-700",
        buttonColor: "bg-purple-600 hover:bg-purple-700",
        lightBg: "bg-purple-50",
        iconBg: "bg-purple-100",
        progressLabel: "Trip in progress"
      };
    }
    return { 
      icon: Compass, 
      label: "Maps", 
      shortLabel: "Maps",
      description: "", 
      color: "from-gray-600 to-gray-700",
      badgeColor: "bg-gray-100 text-gray-700",
      buttonColor: "bg-gray-700 hover:bg-gray-800",
      lightBg: "bg-gray-50",
      iconBg: "bg-gray-100",
      progressLabel: "Navigation"
    };
  };

  // Format time
  const formatTime = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${Math.round(mins)}m`;
  };

  // Format distance
  const formatDistance = (km) => {
    if (!km) return null;
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
  };

  // Don't render if not EN or IP stage
  if (!shouldShow) return null;

  const stageInfo = getStageInfo();
  const StageIcon = stageInfo.icon;

  return (
    <>
      {/* Floating Bubble - Mobile Optimized */}
      <div
        ref={bubbleRef}
        className={`fixed z-[9998] select-none touch-none ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transition: isDragging ? "none" : "left 0.2s ease-out, top 0.2s ease-out",
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        {/* Drag handle indicator for mobile */}
        {isMobile && !isDragging && (
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gray-400/50 rounded-full px-2 py-0.5 opacity-60">
            <Move size={10} className="text-white" />
          </div>
        )}

        {/* Main Bubble */}
        <div
          onClick={() => !isDragging && setIsExpanded(true)}
          className={`
            relative flex items-center justify-center
            ${isMobile ? 'w-12 h-12' : 'w-14 h-14 sm:w-16 sm:h-16'}
            rounded-full shadow-lg
            bg-gradient-to-br ${stageInfo.color}
            border-2 border-white
            ${isPulsing ? "animate-pulse" : ""}
            transition-all duration-300 ease-out
            hover:shadow-xl active:scale-95
            cursor-pointer
          `}
        >
          {/* Pulse ring effect */}
          <div
            className={`
              absolute inset-0 rounded-full
              bg-gradient-to-br ${stageInfo.color}
              animate-ping opacity-30
            `}
          />

          {/* Icon */}
          <StageIcon 
            size={isMobile ? 18 : 20} 
            className="text-white" 
            strokeWidth={1.8}
          />

          {/* Stage badge - small mobile badge */}
          <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white text-gray-800 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[10px] font-semibold shadow-md whitespace-nowrap ${isMobile ? 'text-[8px]' : ''}`}>
            {isMobile ? stageInfo.shortLabel : stageInfo.label}
          </div>
        </div>
      </div>

      {/* Modal Popup - Uber Style Bottom Sheet for Mobile */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setIsExpanded(false)}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag indicator for mobile */}
            <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mt-3 sm:hidden" />
            
            {/* Close button for desktop */}
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-4 right-4 p-1.5 bg-white/90 rounded-full shadow-sm hidden sm:block"
            >
              <X size={16} className="text-gray-500" />
            </button>

            {/* Modal Header */}
            <div className={`p-4 sm:p-5 border-b border-gray-100 ${stageInfo.lightBg}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${stageInfo.iconBg}`}>
                  <StageIcon size={isMobile ? 18 : 20} className={stageInfo.badgeColor.split(' ')[1]} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      {stageInfo.label}
                    </h3>
                    {isMobile && (
                      <button
                        onClick={() => setIsExpanded(false)}
                        className="p-1.5 -mr-1.5"
                      >
                        <X size={18} className="text-gray-400" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {stageInfo.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-5 space-y-4 max-h-[70vh] sm:max-h-[80vh] overflow-y-auto">
              {/* ETA and Distance Info */}
              {(estimatedTime || estimatedDistance) && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-500">Estimated arrival</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatTime(estimatedTime)}
                    </span>
                  </div>
                  {estimatedDistance && (
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <Navigation2 size={14} className="text-gray-400" />
                        <span className="text-xs text-gray-500">Distance</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDistance(estimatedDistance)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Progress Indicator */}
              <div className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${isEnRoute ? 'bg-blue-500' : 'bg-purple-500'} animate-pulse`} />
                  <span className="text-xs font-medium text-gray-700">{stageInfo.progressLabel}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full transition-all duration-500 ${isEnRoute ? 'bg-blue-500' : 'bg-purple-500'}`}
                    style={{ width: isEnRoute ? '35%' : '70%' }}
                  />
                </div>
              </div>

              {/* Route Info */}
              <div className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex gap-3">
                  {/* Route line */}
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-emerald-100"></div>
                    <div className="w-0.5 h-12 bg-gradient-to-b from-gray-300 to-gray-200"></div>
                    <div className="w-3 h-3 rounded-full bg-red-500 ring-2 ring-red-100"></div>
                  </div>
                  
                  {/* Route text */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">From</p>
                      <p className="text-sm font-medium text-gray-900">
                        {isEnRoute ? "Your Location" : "Pickup Point"}
                      </p>
                      {isEnRoute && pickupLocation && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {pickupLocation.lat?.toFixed(4)}, {pickupLocation.lng?.toFixed(4)}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">To</p>
                      <p className="text-sm font-medium text-gray-900">
                        {isEnRoute ? "Pickup Point" : "Destination"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                {/* Open Maps Button */}
                <button 
                  className={`w-full py-3 sm:py-3.5 rounded-xl ${stageInfo.buttonColor} text-white font-semibold text-sm flex items-center justify-center gap-2 transition shadow-sm active:scale-[0.98]`}
                  onClick={handleOpenMaps}
                >
                  <Navigation size={16} />
                  <span>Open Navigation</span>
                  <ChevronRight size={14} />
                </button>

                {/* Emergency Button */}
                <button 
                  className="w-full py-3 sm:py-3.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-semibold text-sm flex items-center justify-center gap-2 transition border border-red-100 active:scale-[0.98]"
                  onClick={handleEmergency}
                >
                  <AlertTriangle size={16} />
                  <span>Emergency Alert</span>
                </button>
              </div>

              {/* Safety Note */}
              <div className="flex items-center justify-center gap-1.5 pt-2 pb-1">
                <Shield size={12} className="text-gray-400" />
                <p className="text-[10px] text-gray-400 text-center">
                  Stay safe • Share your trip status
                </p>
              </div>
            </div>

            {/* Safe area spacer for mobile */}
            {isMobile && <div className="h-4" />}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }

        /* Mobile touch optimizations */
        @media (max-width: 768px) {
          .active\\:scale-\\[0\\.98\\]:active {
            transform: scale(0.98);
          }
        }
      `}</style>
    </>
  );
};

export default HireAssistanceBubble;